// append a new star rating by using: createStarRating({ size: X, rating: X })
import "./index.scss"

require.context("./static", true, /\.svg$/)
interface StarRatingOptions {
  size?: number
  rating: number
  windState?: string
}

const WHITE_STARS = ["on", "cross-on", "cross-<br/>on", "cross-<BR>on", "cross"]

export function starIsWhite(windState: string, rating: number) {
  return rating < 0 || WHITE_STARS.includes(windState)
}

export default function createStarRating({
  size = 32,
  rating,
  windState,
}: StarRatingOptions): SVGElement {
  const starSize = { width: size, height: size }
  let color: string = "hsl(0, 0%, 100%)"
  let stroke: number = 1
  let weight: number = 400
  const scale = rating < 6 ? 0.8 : 1
  const translate = rating < 6 ? 4 : 0
  const dangerous = rating > 10
  let textColor: string

  const starText = dangerous ? "!" : Math.abs(rating)
  const whiteStar = starIsWhite(windState, rating)

  switch (rating) {
    case 1:
      color = "hsl(57.1, 100%, 79.4%)"
      break
    case 2:
      color = "hsl(54.7, 100%, 77.8%)"
      break
    case 3:
      color = "hsl(53.3, 100%, 73.5%)"
      break
    case 4:
      color = "hsl(52, 100%, 70.6%)"
      break
    case 5:
      color = "hsl(50.9, 100%, 67.6%)"
      weight = 600
      break
    case 6:
      color = "hsl(50, 100%, 64.7%)"
      weight = 600
      break
    case 7:
      color = "hsl(49.2, 100%, 61.8%)"
      stroke = 2
      weight = 600
      break
    case 8:
      color = "hsl(49.2, 100%, 61.8%)"
      stroke = 2
      weight = 800
      break
    case 9:
      color = "hsl(48, 100%, 55.9%)"
      stroke = 2
      weight = 800
      break
    case 10:
      color = "hsl(47.5, 100%, 52.9%)"
      stroke = 0
      weight = 800
      break
    case 11:
      color = "hsl(0, 100%, 40.8%)"
      weight = 800
      break
    default:
      color = "hsl(0, 0%, 100%)"
      weight = 400
      break
  }

  if (dangerous) {
    textColor = "hsl(0, 0%, 100%)"
  } else if (rating === 0) {
    textColor = "hsl(0, 0%, 60%)"
  } else {
    textColor = "hsl(0, 0%, 0%)"
  }

  color = whiteStar && rating <= 10 ? "hsl(0, 0%, 100%)" : color

  const star = document.createElementNS("http://www.w3.org/2000/svg", "svg")
  star.setAttribute("viewBox", "0 0 37 33")
  star.setAttribute("width", `${starSize.width}`)
  star.setAttribute("height", `${starSize.height}`)

  const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
  if (dangerous) {
    path.setAttribute("d", "m21 1.63 10.5 18.18 10.5 18.19h-21-21l10.5-18.19z")
    path.setAttribute("fill", color)
    path.setAttribute("transform", "scale(0.8)")
  } else {
    path.setAttribute(
      "d",
      "m6.08 30.43c-.02 1.3.95 2.05 1.96 1.75.29-.08.57-.21.84-.36 1.78-.98 3.54-1.98 5.32-2.95 1.14-.62 2.29-1.23 3.44-1.82.55-.28 1.13-.23 1.68.01.55.25 1.1.51 1.64.81 2.13 1.16 4.24 2.35 6.37 3.51.57.31 1.15.61 1.76.82.72.25 1.38-.1 1.7-.8.27-.57.22-1.14.03-1.72-.52-1.58-1.06-3.15-1.55-4.74-.42-1.34-.87-2.68-1.03-4.09-.08-.71.08-1.34.7-1.77 1.96-1.37 3.93-2.73 5.89-4.12.56-.4 1.13-.8 1.61-1.28.53-.52.52-1.2.27-1.88-.22-.59-.74-.74-1.28-.82-.92-.14-1.85-.27-2.77-.4-1.42-.2-2.83-.37-4.25-.59-1.13-.17-2.26-.38-3.38-.6-.53-.1-.91-.44-1.21-.9-1.25-1.97-2.25-4.06-3.22-6.17-.23-.5-.48-1.02-.82-1.45-.72-.9-1.97-.81-2.61.14-.15.23-.29.47-.41.71-1.11 2.11-2.22 4.22-3.32 6.33-.51.98-1.29 1.52-2.39 1.65-1.2.14-2.39.31-3.59.48-1.17.16-2.35.33-3.52.51-.83.13-1.67.23-2.49.42-1.14.28-1.56 1.53-.87 2.48.15.21.34.41.55.56 1.33.98 2.68 1.94 4.02 2.91.99.71 1.99 1.4 2.98 2.11.5.37.73.9.65 1.52-.07.54-.12 1.1-.27 1.62-.66 2.25-1.35 4.49-2.02 6.73-.16.53-.31 1.07-.39 1.38h-.02z",
    )
    path.setAttribute("fill", color)
    path.setAttribute("stroke", starText === 0 ? "hsl(0, 0%, 83%)" : "hsl(0, 0%, 0%)")
    path.setAttribute("stroke-width", `${stroke}px`)
    path.setAttribute("transform", `translate(${translate}, ${translate}) scale(${scale})`)
  }
  const text = document.createElementNS("http://www.w3.org/2000/svg", "text")
  text.setAttribute("x", "50%")
  text.setAttribute("y", "56%")
  text.setAttribute("font-weight", `${weight}`)
  text.setAttribute("fill", `${textColor}`)
  text.setAttribute("dominant-baseline", "middle")
  text.setAttribute("text-anchor", "middle")
  text.textContent = `${starText}`
  text.setAttribute("font-size", "18")

  if (dangerous) {
    text.setAttribute("transform", "scale(0.8)")
  } else {
    text.setAttribute("transform", `translate(${translate}, ${translate}) scale(${scale})`)
  }

  star.appendChild(path)
  star.appendChild(text)

  return star
}
